/**
 * Neon Void Scene — dense starfield, constellation patterns, shooting stars,
 * and a massive space station silhouette with neon wireframe edges.
 */

import { Container, Graphics } from 'pixi.js';
import type { StageScene, ParallaxLayers } from '../stage-scene';
import type { StageTheme } from '../../../stages';
import { WORLD } from '../../../constants';
import { Star } from '../particles/star';
import { buildStation, StationLight } from '../builders/neon-builder';

interface ShootingStar { gfx: Graphics; x: number; y: number; vx: number; vy: number; life: number; maxLife: number; trail: Graphics }
interface ConstellationLine { gfx: Graphics }

export class NeonVoidScene implements StageScene {
  private stars: Star[] = [];
  private shootingStars: ShootingStar[] = [];
  private constellationLines: ConstellationLine[] = [];
  private stationLights: StationLight[] = [];
  private stationContainer: Container | null = null;
  private armContainerA: Container | null = null;
  private armContainerB: Container | null = null;
  private layers: ParallaxLayers | null = null;
  private shootTimer = 0;
  private armRotationA = 0;
  private armRotationB = 0;
  private driftX = 0;

  build(layers: ParallaxLayers, theme: StageTheme): void {
    this.layers = layers;
    const W = WORLD.VIRTUAL_WIDTH;
    const H = WORLD.VIRTUAL_HEIGHT;

    // ── Sky Layer ──

    // RGB accent palette for special stars
    const rgbAccents = [0xff4466, 0x44ff88, 0x4488ff, 0xff44dd, 0x44ffff, 0xffaa22];

    // Dense starfield (600 stars across upper 70%)
    for (let i = 0; i < 600; i++) {
      const x = Math.random() * W;
      const y = Math.random() * H * 0.7;
      const isRgb = Math.random() < 0.08; // ~8% are RGB accented
      const size = isRgb ? (1.0 + Math.random() * 2.0) : (0.4 + Math.random() * 1.8);
      const color = isRgb
        ? rgbAccents[Math.floor(Math.random() * rgbAccents.length)]
        : theme.starColors[Math.floor(Math.random() * theme.starColors.length)];
      const baseAlpha = isRgb ? (0.5 + Math.random() * 0.5) : (0.35 + Math.random() * 0.65);

      const star = new Star(layers.sky.container, {
        x, y, size, color, baseAlpha,
        twinkleSpeed: isRgb ? (0.8 + Math.random() * 1.5) : (1.5 + Math.random() * 3),
        halo: isRgb ? { sizeMult: 2.5, alpha: 0.08 } : undefined,
      });
      this.stars.push(star);
    }

    // Constellation patterns (5 constellations, 3-6 stars each connected by dotted lines)
    const constellations = [
      { cx: W * 0.12, cy: H * 0.1, points: this.generateConstellation(5) },
      { cx: W * 0.3, cy: H * 0.05, points: this.generateConstellation(4) },
      { cx: W * 0.55, cy: H * 0.08, points: this.generateConstellation(6) },
      { cx: W * 0.75, cy: H * 0.12, points: this.generateConstellation(3) },
      { cx: W * 0.9, cy: H * 0.06, points: this.generateConstellation(5) },
    ];
    for (const c of constellations) {
      // Draw connecting lines
      const lineGfx = new Graphics();
      for (let i = 0; i < c.points.length - 1; i++) {
        const p1 = c.points[i];
        const p2 = c.points[i + 1];
        // Dotted line effect
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        const dots = Math.floor(len / 6);
        for (let d = 0; d <= dots; d++) {
          const t = d / Math.max(dots, 1);
          lineGfx.circle(c.cx + p1.x + dx * t, c.cy + p1.y + dy * t, 0.5);
        }
      }
      lineGfx.fill({ color: 0x4466aa, alpha: 0.25 });
      layers.sky.container.addChild(lineGfx);
      this.constellationLines.push({ gfx: lineGfx });

      // Draw constellation stars (brighter than field stars)
      for (const p of c.points) {
        this.stars.push(new Star(layers.sky.container, {
          x: c.cx + p.x,
          y: c.cy + p.y,
          size: 1.5 + Math.random() * 0.5,
          color: 0xddeeff,
          baseAlpha: 0.8,
          twinkleSpeed: 2
        }));
      }
    }

    // ── Deep Layer: Space Station ──
    const station = buildStation(layers, W, H);
    this.stationContainer = station.stationContainer;
    this.armContainerA = station.armContainerA;
    this.armContainerB = station.armContainerB;
    this.stationLights = station.stationLights;

    // ── Mid Layer: Shooting stars spawned dynamically ──
    // (nothing static, they're created in update())
  }

  private generateConstellation(count: number): { x: number; y: number }[] {
    const points: { x: number; y: number }[] = [];
    let x = 0, y = 0;
    for (let i = 0; i < count; i++) {
      points.push({ x, y });
      x += 15 + Math.random() * 40;
      y += (Math.random() - 0.5) * 30;
    }
    return points;
  }

  update(dt: number, elapsed: number, wind?: number): void {
    const W = WORLD.VIRTUAL_WIDTH;
    const H = WORLD.VIRTUAL_HEIGHT;

    // ── Arm rotation + wind drift ──
    if (this.stationContainer) {
      // Wind drift on the whole station
      const DRIFT_SCALE = 8500;
      const LERP_RATE = 0.08;
      const targetX = (wind ?? 0) * DRIFT_SCALE;
      this.driftX += (targetX - this.driftX) * LERP_RATE * dt;
      this.stationContainer.x = WORLD.VIRTUAL_WIDTH * 0.5 + this.driftX;
    }
    // Independent arm rotation — slow, different speeds for organic feel
    if (this.armContainerA) {
      this.armRotationA += 0.02 * dt; // ~314s per revolution
      this.armContainerA.rotation = this.armRotationA;
    }
    if (this.armContainerB) {
      this.armRotationB += 0.013 * dt; // ~483s per revolution
      this.armContainerB.rotation = this.armRotationB;
    }

    // Star twinkle (varied speeds for organic feel)
    for (const star of this.stars) {
      star.update(elapsed);
    }

    // Station lights pulse
    for (const light of this.stationLights) {
      const t = Math.sin(elapsed * light.speed + light.phase);
      if (light.rgb) {
        // RGB lights: sharper on/off blink with brighter peaks
        const blink = Math.pow(0.5 + 0.5 * t, 2);
        light.gfx.alpha = 0.15 + 0.85 * blink;
      } else {
        light.gfx.alpha = 0.3 + 0.7 * (0.5 + 0.5 * t);
      }
    }

    // Shooting stars — spawn periodically
    this.shootTimer -= dt;
    if (this.shootTimer <= 0) {
      this.spawnShootingStar(W, H);
      this.shootTimer = 2 + Math.random() * 4; // Every 2-6 seconds
    }

    // Update active shooting stars
    for (let i = this.shootingStars.length - 1; i >= 0; i--) {
      const s = this.shootingStars[i];
      s.life -= dt;
      s.x += s.vx * dt;
      s.y += s.vy * dt;

      const lifeRatio = Math.max(0, s.life / s.maxLife);

      // Head position
      s.gfx.x = s.x;
      s.gfx.y = s.y;
      s.gfx.alpha = lifeRatio;

      // Trail — fades as life decreases
      s.trail.clear();
      const trailLen = 80 * lifeRatio;
      const nx = -s.vx; const ny = -s.vy;
      const mag = Math.sqrt(nx * nx + ny * ny);
      if (mag > 0) {
        const dirX = nx / mag;
        const dirY = ny / mag;
        s.trail.moveTo(s.x, s.y);
        s.trail.lineTo(s.x + dirX * trailLen, s.y + dirY * trailLen);
        s.trail.stroke({ color: 0xaaccff, alpha: lifeRatio * 0.4, width: 1.5 });
      }

      if (s.life <= 0) {
        s.gfx.destroy();
        s.trail.destroy();
        this.shootingStars.splice(i, 1);
      }
    }
  }

  private spawnShootingStar(W: number, H: number): void {
    if (!this.layers) return;

    const gfx = new Graphics();
    const trail = new Graphics();

    // Spawn from upper portion, travel diagonally
    const x = Math.random() * W * 0.8 + W * 0.1;
    const y = Math.random() * H * 0.15;
    const angle = Math.PI * 0.15 + Math.random() * 0.3; // Shallow diagonal
    const speed = 400 + Math.random() * 300;
    const vx = Math.cos(angle) * speed;
    const vy = Math.sin(angle) * speed;

    gfx.circle(0, 0, 1.5);
    gfx.fill({ color: 0xffffff, alpha: 1.0 });
    gfx.x = x;
    gfx.y = y;

    this.layers.sky.container.addChild(trail);
    this.layers.sky.container.addChild(gfx);

    const maxLife = 0.4 + Math.random() * 0.4;
    this.shootingStars.push({ gfx, x, y, vx, vy, life: maxLife, maxLife, trail });
  }

  onExplosion(): void {
    // No reactive elements
  }

  destroy(): void {
    for (const s of this.shootingStars) {
      s.gfx.destroy();
      s.trail.destroy();
    }
    this.stars = [];
    this.shootingStars = [];
    this.constellationLines = [];
    this.stationLights = [];
    if (this.layers) {
      this.layers.sky.clear();
      this.layers.deep.clear();
      this.layers.mid.clear();
    }
  }
}
